import type { MaybeRefOrGetter, Ref } from 'vue';

import { useResizeObserver } from '@vueuse/core';
import { computed, nextTick, onMounted, ref, toValue, watch } from 'vue';

export interface NavPlaceIndicatorRect {
  left: number;
  top: number;
  width: number;
  height: number;
  barWidth: number;
  barHeight: number;
}

interface Options {
  /** Whether the bar draws an indicator at all; off, it never measures. */
  enabled: MaybeRefOrGetter<boolean>;
  /** The current place's link target; a change of it is the one thing that slides. */
  currentPlace: MaybeRefOrGetter<string | undefined>;
  motionAllowed: MaybeRefOrGetter<boolean>;
}

/**
 * Where a nav bar's current-place fill sits: measured from the link marked `data-current-place`
 * inside `bar`. Only a change of place slides it. Mounting, a resize, or the links shifting under
 * it (a place added after mount, a late font) snap it, so it never trails its label, and it is
 * placed without a transition whenever it reappears. Below the breakpoint that hides the bar every
 * link measures zero, so the rect is `null` until the bar is shown again.
 */
export const useNavPlaceIndicator = (bar: Readonly<Ref<HTMLElement | null>>, options: Options) => {
  const rect = ref<NavPlaceIndicatorRect | null>(null);
  const armed = ref(false);

  const slides = computed(() => armed.value && toValue(options.motionAllowed));

  const place = (slide: boolean) => {
    const el = bar.value;
    const current = toValue(options.enabled) ? el?.querySelector<HTMLElement>('[data-current-place]') : null;
    if (!el || !current?.offsetParent) {
      rect.value = null;
      armed.value = false;

      return;
    }

    const snap = !slide || rect.value === null;
    if (snap) {
      armed.value = false;
    }
    rect.value = {
      left: current.offsetLeft,
      top: current.offsetTop,
      width: current.offsetWidth,
      height: current.offsetHeight,
      barWidth: el.clientWidth,
      barHeight: el.clientHeight,
    };
    if (snap) {
      requestAnimationFrame(() => {
        armed.value = true;
      });
    }
  };

  const fillStyle = computed(() => {
    const r = rect.value;

    return r ? { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` } : undefined;
  });

  // Clips a layer laid over the whole bar to the fill's shape: a pill, so its radius is half its height.
  const clipStyle = computed(() => {
    const r = rect.value;

    return r
      ? { clipPath: `inset(${r.top}px ${r.barWidth - r.left - r.width}px ${r.barHeight - r.top - r.height}px ${r.left}px round ${r.height / 2}px)` }
      : undefined;
  });

  watch(() => toValue(options.currentPlace), () => nextTick(() => place(true)), { flush: 'post' });
  useResizeObserver(bar, () => place(false));
  onMounted(() => place(false));

  return { rect, slides, fillStyle, clipStyle };
};
